import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useHistory } from 'react-router'
import { useDispatch } from 'react-redux'
import { MapItemType, MapType } from './Map'
import getPathForSpace from '../../../utils/spaces/getPathForSpace'
import useParams from '../../../hooks/useParams'
import { _ } from '../../../utils/localization'
import SwipeHand from '../../../assets/swipe-hand.svg'
import colors from '../../../constants/colors'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import MobileFooter from '../components/MobileFooter'
import useSlug from '../../../hooks/useSlug'
import handleWidgetClick from '../handleWidgetClick'
import EmbeddedVideo from '../../embeddedvideo/EmbeddedVideo'

type PointStyleProps = {
    x: number
    y: number
    mapHeight: number
    mapWidth: number
    imageHeight: number
    imageWidth: number
}

type EmbeddedVideoStyleProps = {
    top: number
    left: number
    width: number
    height: number
}

const S = {
    Wrapper: styled.div`
        position: relative;
        height: 100%;
        width: 100%;
        overflow: hidden;
    `,
    Map: styled.div`
        position: absolute;
        top: 0;
        left: 0;
        bottom: -20px;
        right: -20px;
        overflow: scroll;
    `,
    EmbeddedVideoWrapper: styled.div<EmbeddedVideoStyleProps>`
        position: absolute;
        top: ${({ top }) => top}px;
        left: ${({ left }) => left}px;
        width: ${({ width }) => width}px;
        height: ${({ height }) => height}px;
        z-index: 2;
        overflow: hidden;
        background: #000;

        & > iframe {
            width: 100%;
            height: 100%;
            border: 0;
        }
    `,
    Point: styled.div<PointStyleProps>`
        position: absolute;
        top: ${({ y, mapHeight, imageHeight }) => (y * mapHeight) / imageHeight}px;
        left: ${({ x, mapWidth, imageWidth }) => (x * mapWidth) / imageWidth}px;
        width: 20px;
        height: 20px;
        transform: translate(-50%, -50%);
        background: white;
        box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.5);
        z-index: 1;
        border-radius: 50%;
        border: 1px solid #989898;
        &::before,
        &::after {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 26px;
            height: 26px;
            border-radius: 50%;
            border: 1px solid rgba(255, 255, 255, 0.5);
        }
        &::before {
            width: 32px;
            height: 32px;
            border: 1px solid rgba(255, 255, 255, 0.25);
        }
    `,
    HintWrapper: styled.div`
        position: fixed;
        top: 58px;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1;
        color: white;
        background: rgba(0, 0, 0, 0.53);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        text-align: center;
    `,
    HintIcon: styled.img``,
    HintTitle: styled.h1`
        font-weight: 700;
        font-size: 24px;
        line-height: 28px;
        opacity: 0.9;
        text-shadow: 0px 0px 4px rgba(255, 255, 255, 0.5);
    `,
    HintDescription: styled.p`
        color: rgba(255, 255, 255, 0.75);
        font-size: 14px;
        line-height: 20px;
        opacity: 0.75;
    `,
    HintFooter: styled.h3`
        margin-top: 32px;
        font-weight: 700;
        font-size: 18px;
        line-height: 20px;
        text-shadow: 0px 0px 4px rgba(255, 255, 255, 0.5);
        text-transform: uppercase;
    `,
    AreaTitle: styled.div`
        position: absolute;
        bottom: 10px;
        left: 50%;
        transform: translate(-50%, 52px);
        max-width: 200px;
        min-width: 60px;
        padding: 6px 8px;
        background: ${colors.darkTitle};
        border: 1px solid #ffffff;
        box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.3);
        border-radius: 5px;
        color: white;
        opacity: 0.9;
        font-weight: 500;
        font-size: 12px;
    `,
    ActionsFooter: styled.div`
        position: fixed;
        bottom: 0;
        left: 0;
        padding-bottom: 10px;
        width: 100vw;
        height: 42px;
        background: #343A40;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 14px;
    `,
    Action: styled.div`
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
    `,
    Divider: styled.div`
        height: 20px;
        width: 0;
        border: 1px solid #F9F9F9;
        opacity: 0.5;
    `
}

const getActionKey = (item: MapItemType) => {
    if (item.space_slug) {
        return `space:${item.space_slug}`
    }
    if (item.space) {
        return `space:${item.space}`
    }
    if (item.widget === 'embedded' && item.embedded_url) {
        return `embedded:${item.embedded_url}`
    }
    if (item.widget && item.widget_link) {
        return `${item.widget}:${item.widget_link}`
    }
    if (item.widget) {
        return `widget:${item.widget}`
    }
    return `item:${item.id}`
}

const getPointCoordinates = (item: MapItemType) => {
    const coords = item.coords || []
    if (!coords.length) {
        return { x: 0, y: 0 }
    }

    // Mobile marker items usually have a single x,y point.
    if (item.for_mobile || coords.length <= 2) {
        return { x: coords[0], y: coords[1] }
    }

    let totalX = 0
    let totalY = 0
    let pairs = 0
    for (let i = 0; i < coords.length - 1; i += 2) {
        totalX += coords[i]
        totalY += coords[i + 1]
        pairs += 1
    }

    if (!pairs) {
        return { x: coords[0], y: coords[1] }
    }

    return {
        x: totalX / pairs,
        y: totalY / pairs,
    }
}

const getAreaEdges = (coords: number[]) => {
    const xCoords = coords.filter((coord, idx) => idx % 2 === 0)
    const yCoords = coords.filter((coord, idx) => idx % 2 === 1)
    return {
        xMin: Math.min(...xCoords),
        xMax: Math.max(...xCoords),
        yMin: Math.min(...yCoords),
        yMax: Math.max(...yCoords),
    }
}

const getEmbeddedSpacing = (
    item: MapItemType,
    mapWidth: number,
    mapHeight: number,
    imageWidth: number,
    imageHeight: number,
) => {
    if (!item.coords || item.coords.length < 4 || !mapWidth || !mapHeight) {
        return null
    }

    const edges = getAreaEdges(item.coords)
    const scaleX = mapWidth / imageWidth
    const scaleY = mapHeight / imageHeight

    return {
        top: edges.yMin * scaleY,
        left: edges.xMin * scaleX,
        width: (edges.xMax - edges.xMin) * scaleX,
        height: (edges.yMax - edges.yMin) * scaleY,
    }
}

const MobileMap = ({ map }: { map: MapType }) => {
    const dispatch = useDispatch()
    const eventSlug = useSlug('eventSlug')
    const { width: screenWidth, height: screenHeight } = useWindowDimensions()
    const isLandscape = screenWidth > screenHeight
    const ref = useRef<HTMLDivElement>()
    const [mapDimension, setMapDimension] = useState({ width: 0, height: 0 })
    const isShownHint = localStorage.getItem(`${eventSlug}__mobile_hint`)
    const [showHint, setShowHint] = useState(false)
    const [showAreaTitle, setShowAreaTitle] = useState(false)
    const mapItems = map.items || []

    const mobileItems = React.useMemo(() => mapItems.filter((item) => item.for_mobile), [mapItems])

    const embeddedItems = React.useMemo(() => mapItems.filter((item) => (
        !item.for_mobile
        && item.widget === 'embedded'
        && Boolean(item.embedded_url)
    )), [mapItems])

    const ratio = isLandscape
        ? map.image_height / map.image_width
        : map.image_width / map.image_height
    const history = useHistory()
    const params = useParams()

    useEffect(() => {
        if (ref.current) {
            isLandscape ? setMapDimension({
                width: ref.current.clientWidth,
                height: ref.current.clientWidth * ratio,
            }) : setMapDimension({
                width: ref.current.clientHeight * ratio,
                height: ref.current.clientHeight,
            })

            if (isLandscape) {
                const offsetHeight = ref.current.clientWidth * ratio - (screenHeight - 58)
                if (offsetHeight > 0) {
                    ref.current.scrollTop = offsetHeight / 2
                }
            }
        }
    }, [ratio, isLandscape, map.image_height, screenHeight])

    const hideHint = (e) => {
        e.stopPropagation()
        localStorage.setItem(`${eventSlug}__mobile_hint`, 'true')
        setShowAreaTitle(false)
        setShowHint(true)
    }

    const goToSpace = (item) => {
        history.push(
            getPathForSpace(
                {
                    type: item.space_type,
                    slug: item.space_slug,
                },
                params
            )
        )
    }

    const handleClick = (e, item: MapItemType) => {
        e.stopPropagation()
        if (item.space) {
            return goToSpace(item)
        }
        return dispatch(handleWidgetClick(item))
    }

    return (
        <S.Wrapper>
            <S.Map ref={ref} onClick={() => setShowAreaTitle(!showAreaTitle)}>
                {screenHeight > screenWidth ? (
                    <img
                        src={map.meta.image}
                        alt="map"
                        width={`${ratio * ref.current?.clientHeight}px`}
                        height="100%"
                    />
                ) : (
                    <img
                        src={map.meta.image}
                        alt="map"
                        height={`${ratio * ref.current?.clientWidth}px`}
                        width="100%"
                    />
                )}
                {embeddedItems.map((item) => {
                    const spacing = getEmbeddedSpacing(
                        item,
                        mapDimension.width,
                        mapDimension.height,
                        map.image_width,
                        map.image_height,
                    )
                    if (!spacing) {
                        return null
                    }

                    return (
                        <S.EmbeddedVideoWrapper
                            key={`embedded-video-${item.id}`}
                            top={spacing.top}
                            left={spacing.left}
                            width={spacing.width}
                            height={spacing.height}
                        >
                            <EmbeddedVideo url={item.embedded_url} />
                        </S.EmbeddedVideoWrapper>
                    )
                })}
                {(!isShownHint && !showHint) ? (
                    <S.HintWrapper onClick={hideHint}>
                        <S.HintIcon src={SwipeHand} />
                        <S.HintTitle>
                            {_('map_mobile.swipe_to_navigate')}
                        </S.HintTitle>
                        <S.HintDescription>
                            {_('map_mobile.swipe_to_navigate_description')}
                        </S.HintDescription>
                        <S.HintFooter>
                            {_('map_mobile.tap_to_continue')}
                        </S.HintFooter>
                    </S.HintWrapper>
                ) : (
                    mobileItems
                        .map((item) => {
                            const { x, y } = getPointCoordinates(item)
                            return (
                                <S.Point
                                    key={item.id}
                                    x={x}
                                    y={y}
                                    mapHeight={mapDimension.height}
                                    mapWidth={mapDimension.width}
                                    imageHeight={map.image_height}
                                    imageWidth={map.image_width}
                                    onClick={(e) => handleClick(e, item)}
                                >
                                    {showAreaTitle && (
                                        <S.AreaTitle className="text-cut">
                                            {item.space_name || item.name}
                                        </S.AreaTitle>
                                    )}
                                </S.Point>
                            )
                        })
                )}
            </S.Map>
            <MobileFooter map={map} />
        </S.Wrapper>
    )
}

export default MobileMap
